import React from 'react';
import { useTranslation } from 'react-i18next';

import { Callout, Icon, SectionTitle, Text } from '../../../../components';
import { useThemeMode } from '../../../../theme/OwsTheme';
import { CUST_GEN_DOT_FILL, CUST_GEN_DOT_FILL_LIGHT } from '../../../../theme/customizer-tokens';
import { DotField } from '../../../customizer/components/DotField';
import { GenerateButton } from '../../../creatives/components/GenerateButton';
import { RunStatusLine } from '../../../creatives/components/StoryboardComposer/RunStatusLine';
import { PREVIEW_FIELD_LANDSCAPE, type ArtStyle } from '../../art-style-api';
import type { PreviewCard, PreviewsView } from '../../sample-jobs';
import { evergreenLabel } from '../../site-regen';
import type { ArtStyleBusy } from '../../useArtStyle';
import {
  Root,
  Head,
  HeadCopy,
  Lead,
  PreviewNote,
  Media,
  Img,
  Placeholder,
  PlaceholderText,
  Overlay,
  Backdrop,
  OverlayCenter,
  OverlayText,
  Foot,
  SlotName,
  SlotMeta,
} from './SamplesPanel.styles';
import { TitleRow, OutdatedTag, HeadAction, Notice, PreviewGrid, PreviewCardRoot } from './PreviewsPanel.styles';

export type PreviewsPanelProps = {
  style: ArtStyle;
  /** The batch the cards show (useArtStyle's `previewsView`). */
  view: PreviewsView;
  busy: ArtStyleBusy;
  /** Lumio is extracting the style: a new batch follows it, so nothing else starts meanwhile. */
  extracting: boolean;
  /** Credits a batch costs (four samples); undefined until the price table loads. */
  cost: number | undefined;
  /** The newest extraction failed: a new batch would draw the earlier style, not the latest ingredients. */
  extractFailed?: boolean;
  /** Ask for a new batch of four. The caller runs the affordability gate. */
  onRegenerate: () => void;
};

type PreviewCardViewProps = {
  card: PreviewCard;
  /** CSS aspect-ratio and its label, the batch's one format. */
  ratio: string;
  aspect: string;
};

/**
 * One preview across its lifecycle, in a slot of the batch's format (so nothing moves as it draws): waiting or
 * drawing (the Lumio dot field with what Lumio does, over the preview it replaces when one shows), drawn, didn't draw
 * (refunded on its own; a new batch is the only redraw), stopped, not confirmed, or not drawn yet.
 */
const PreviewCardView = ({ card, ratio, aspect }: PreviewCardViewProps): React.ReactElement => {
  const { t } = useTranslation();
  const [loaded, setLoaded] = React.useState(false);
  const imgRef = React.useRef<HTMLImageElement>(null);
  const dotFill = useThemeMode() === 'light' ? CUST_GEN_DOT_FILL_LIGHT : CUST_GEN_DOT_FILL;

  // A cached image can finish decoding before onLoad attaches.
  React.useEffect(() => {
    setLoaded(Boolean(imgRef.current?.complete));
  }, [card.url]);

  // The evergreen it draws, once casino-creation took the batch; until then its place in the batch.
  // casino-creation names the welcome sample after its source record ("Welcome Bonus Reward Bundles"); the owner
  // reads it as their welcome bonus. The campaign samples keep their own names.
  const label = /^welcome bonus/i.test(card.evergreenName.trim())
    ? t('Welcome bonus')
    : evergreenLabel(card.evergreenName) || card.label || t('Sample {{n}}', { n: card.n });
  const drawing = card.phase === 'queued' || card.phase === 'generating';
  const phrases =
    card.phase === 'queued'
      ? [t('Waiting for its turn')]
      : [t('Painting it in your style'), t('Polishing the details'), t('Adding the final light')];
  let placeholder = '';
  if (card.phase === 'empty') placeholder = t('Not drawn yet');
  else if (card.phase === 'uncertain') placeholder = t('May still be drawing');
  else if (card.phase === 'discarded') placeholder = t('It was stopped.');

  return (
    <PreviewCardRoot>
      <Media $ratio={ratio}>
        {card.url ? (
          <Img
            ref={imgRef}
            src={card.url}
            alt={label}
            $loaded={loaded}
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
          />
        ) : placeholder ? (
          <Placeholder>
            <Icon name="grid" size={18} />
            <PlaceholderText raw text={placeholder} tag="span" />
          </Placeholder>
        ) : null}

        {drawing ? (
          <Overlay>
            <Backdrop $solid={!card.url} />
            <DotField seed={card.n} fill={dotFill} />
            <OverlayCenter>
              {/* Keyed by the phase: a preview that starts drawing types its new line in at once. */}
              <RunStatusLine key={card.phase} stacked phrases={phrases} />
            </OverlayCenter>
          </Overlay>
        ) : null}

        {card.phase === 'failed' ? (
          <Overlay>
            <Backdrop $solid />
            <OverlayCenter role="alert">
              <Icon name="warning" size={18} />
              <OverlayText text="This one didn't draw. Your credits were refunded." tag="span" />
            </OverlayCenter>
          </Overlay>
        ) : null}
      </Media>
      <Foot>
        <SlotName raw text={label} tag="span" />
        <SlotMeta raw text={aspect} tag="span" />
      </Foot>
    </PreviewCardRoot>
  );
};

/**
 * Samples on the casino_creation pipeline: one batch of four draft previews of casino-creation's evergreens drawn in
 * the style, in one format, so the owner can judge it before using it. An extraction draws a batch; "Regenerate
 * samples" draws a new one (four samples' price; a preview that fails is refunded on its own and there is no per-card
 * retry). The panel says when the batch no longer matches the style (Outdated), when casino-creation may or may not
 * have taken it, when it refused it, and when the style needs a rating or a new extraction first. Each preview follows
 * its own creative job, so its progress arrives with the shared jobs feed.
 */
export const PreviewsPanel = ({
  style,
  view,
  busy,
  extracting,
  cost,
  extractFailed = false,
  onRegenerate,
}: PreviewsPanelProps): React.ReactElement => {
  const { t } = useTranslation();

  // A style written before this pipeline has no canonical text to preview: an extraction writes it.
  const legacy = style.art_style.trim() === '';
  const canRegenerate = !extracting && busy === null && !view.inFlight && !legacy && !style.rating_needed;
  // A drawn set that still matches the style needs no redraw: the action shows only when there is none yet, when the
  // style changed since (Outdated), or when the set did not come back whole (refused, not confirmed, a card failed).
  const offerDraw =
    !view.batchId ||
    view.outdated ||
    view.state === 'refused' ||
    view.state === 'uncertain' ||
    view.cards.some((card) => card.phase === 'failed' || card.phase === 'discarded' || card.phase === 'uncertain');
  const landscape = view.fieldName === PREVIEW_FIELD_LANDSCAPE;
  const ratio = landscape ? '2 / 1' : '1 / 1';
  const aspect = landscape ? '2:1' : '1:1';

  // One notice at a time, the one that says what to do next; none while an extraction draws a new batch.
  let notice: React.ReactNode = null;
  if (!extracting) {
    if (style.rating_needed) {
      notice = (
        <Callout
          tone="warning"
          text="Lumio needs to rate this style before it can draw samples. Extract it again to rate it."
        />
      );
    } else if (legacy) {
      notice = <Callout tone="info" text="Extract your style again to draw new samples." />;
    } else if (extractFailed) {
      notice = (
        <Callout
          tone="warning"
          text="Your last extraction didn't finish, so new samples would use your previous style. Extract again to use your latest ingredients."
        />
      );
    } else if (view.state === 'uncertain') {
      notice = (
        <Callout
          tone="info"
          text="Your samples may still be drawing. We couldn't confirm they started, so your credits were refunded. Regenerate samples draws a new set."
        />
      );
    } else if (view.state === 'refused') {
      notice = <Callout tone="warning" text="These samples couldn't be drawn. Your credits were refunded." />;
    }
  }

  return (
    <Root>
      <Head>
        <HeadCopy>
          <TitleRow>
            <SectionTitle title={t('Samples')} icon="grid" />
            {view.outdated && !extracting ? (
              <OutdatedTag title={t('Your style changed since these samples were drawn.')}>
                <Icon name="info" size={12} />
                <Text text="Outdated" tag="span" />
              </OutdatedTag>
            ) : null}
          </TitleRow>
          <Lead text="Four of your promotions drawn in your style, so you can judge it before you use it." tag="span" />
          <PreviewNote>
            <Icon name="info" size={13} />
            <Text text="Previews only: they never appear on your website." tag="span" />
          </PreviewNote>
        </HeadCopy>
        {offerDraw ? (
          <HeadAction>
            <GenerateButton
              compact
              secondary
              label={view.batchId ? t('Regenerate samples') : t('Draw samples')}
              cost={cost}
              disabled={!canRegenerate}
              onClick={() => {
                if (canRegenerate) onRegenerate();
              }}
            />
          </HeadAction>
        ) : null}
      </Head>
      {notice ? <Notice>{notice}</Notice> : null}
      <PreviewGrid>
        {view.cards.map((card) => (
          // Keyed by the batch: a new batch's cards start fresh (and fade their previews in).
          <PreviewCardView key={`${view.batchId || 'none'}-${card.n}`} card={card} ratio={ratio} aspect={aspect} />
        ))}
      </PreviewGrid>
    </Root>
  );
};

export default PreviewsPanel;
